export type ItemKind =
  | "house"
  | "room"
  | "pool"
  | "field"
  | "tree"
  | "toilet"
  | "kitchen"
  | "column"
  | "canopy"
  | "wall";
export type CoverKind = "chapa" | "losa";
export type RoofKind = "tiles" | "grass" | "chapa" | "flat";
export type WallKind = "stucco" | "brick";
export type FieldKind = "f5" | "f7" | "f11" | "custom";
export type OpeningKind = "door" | "window";
export type PlaceMode = "select" | "door" | "window" | ItemKind;

export interface Opening {
  id: string;
  kind: OpeningKind;
  wall: 0 | 1 | 2 | 3;
  t: number;
  height: number;
}

export interface Aperture {
  id: string;
  kind: OpeningKind;
  offset: number;
  width: number;
  height: number;
  sill: number;
}

export interface Item {
  id: string;
  kind: ItemKind;
  name: string;
  x: number;
  z: number;
  rotation: number;
  width: number;
  depth: number;
  height: number;
  parentId?: string;
  roof?: RoofKind;
  wall?: WallKind;
  openings?: Opening[];
  fieldKind?: FieldKind;
  heightEnd?: number;
  /** Room corner heights: +x+z, -x+z, +x-z, -x-z */
  cornerH?: [number, number, number, number];
  cover?: CoverKind;
  hasDoor?: boolean;
  doorWidth?: number;
  doorHeight?: number;
  doorOffset?: number;
  doorSill?: number;
  apertures?: Aperture[];
}

export interface Project {
  version: 2;
  lot: { width: number; depth: number };
  items: Item[];
  hour: number;
  /** Yaw in radians. 0 = north is +Z (sun rises at +X). */
  north: number;
  showInteriors: boolean;
  showNames: boolean;
  showMeasures: boolean;
}

export const ITEM_KINDS: ItemKind[] = [
  "house",
  "room",
  "pool",
  "field",
  "tree",
  "toilet",
  "kitchen",
  "column",
  "canopy",
  "wall",
];

export const FIELD_SIZE: Record<Exclude<FieldKind, "custom">, { width: number; depth: number }> = {
  f5: { width: 40, depth: 20 },
  f7: { width: 50, depth: 30 },
  f11: { width: 90, depth: 45 },
};

export function isAddKind(mode: PlaceMode): mode is ItemKind {
  return (ITEM_KINDS as string[]).includes(mode);
}

export function newId(): string {
  return crypto.randomUUID();
}

interface LegacyV1 {
  version: 1;
  lot: { width: number; depth: number };
  house: {
    width: number;
    depth: number;
    height: number;
    x: number;
    z: number;
    rotation: number;
    roof: "tiles" | "grass";
    wall: WallKind;
    openings: Opening[];
  };
  pool: { visible: boolean; width: number; depth: number; x: number; z: number; rotation: number };
  field: { kind: "none" | "f5" | "f7" | "f11"; x: number; z: number; rotation: number };
  hour: number;
}

export function defaultProject(): Project {
  const houseId = newId();
  return {
    version: 2,
    lot: { width: 40, depth: 60 },
    hour: 10,
    north: 0,
    showInteriors: false,
    showNames: true,
    showMeasures: true,
    items: [
      {
        id: houseId,
        kind: "house",
        name: "Casa",
        width: 12,
        depth: 16,
        height: 3.2,
        x: -6,
        z: -8,
        rotation: 0,
        roof: "tiles",
        wall: "stucco",
        openings: [
          { id: newId(), kind: "door", wall: 0, t: 0.5, height: 0 },
          { id: newId(), kind: "window", wall: 0, t: 0.22, height: 1.05 },
          { id: newId(), kind: "window", wall: 0, t: 0.78, height: 1.05 },
          { id: newId(), kind: "window", wall: 2, t: 0.35, height: 1.05 },
          { id: newId(), kind: "window", wall: 2, t: 0.65, height: 1.05 },
        ],
      },
      {
        id: newId(),
        kind: "room",
        name: "Living",
        parentId: houseId,
        width: 5,
        depth: 6,
        height: 2.6,
        x: -6,
        z: -6,
        rotation: 0,
      },
      {
        id: newId(),
        kind: "pool",
        name: "Pileta",
        width: 8,
        depth: 4,
        height: 0.5,
        x: 10,
        z: -10,
        rotation: 0,
      },
      {
        id: newId(),
        kind: "field",
        name: "Cancha F5",
        width: 40,
        depth: 20,
        height: 0.1,
        x: 0,
        z: 16,
        rotation: 0,
        fieldKind: "f5",
      },
    ],
  };
}

export function migrateProject(raw: unknown): Project {
  if (!raw || typeof raw !== "object") throw new Error("formato");
  const boxed = raw as { kind?: string; data?: unknown; version?: number; items?: unknown };
  if (boxed.kind === "mirancho-project" && boxed.data) return migrateProject(boxed.data);
  const data = boxed;
  if ((data.version === 2 || Array.isArray(data.items)) && Array.isArray((data as Project).items)) {
    const p = data as Project;
    return {
      ...defaultProject(),
      ...p,
      version: 2,
      items: p.items,
      showInteriors: Boolean(p.showInteriors),
      showNames: p.showNames !== false,
      showMeasures: p.showMeasures !== false,
      north: Number.isFinite(Number((p as Project).north)) ? Number((p as Project).north) : 0,
    };
  }
  if (data.version !== 1) throw new Error("formato");
  const v1 = raw as LegacyV1;
  const houseId = newId();
  const items: Item[] = [
    {
      id: houseId,
      kind: "house",
      name: "Casa",
      width: v1.house.width,
      depth: v1.house.depth,
      height: v1.house.height,
      x: v1.house.x,
      z: v1.house.z,
      rotation: v1.house.rotation,
      roof: v1.house.roof,
      wall: v1.house.wall,
      openings: v1.house.openings ?? [],
    },
  ];
  if (v1.pool?.visible) {
    items.push({
      id: newId(),
      kind: "pool",
      name: "Pileta",
      width: v1.pool.width,
      depth: v1.pool.depth,
      height: 0.5,
      x: v1.pool.x,
      z: v1.pool.z,
      rotation: v1.pool.rotation,
    });
  }
  if (v1.field && v1.field.kind !== "none") {
    const size = FIELD_SIZE[v1.field.kind];
    items.push({
      id: newId(),
      kind: "field",
      name: `Cancha ${v1.field.kind.toUpperCase()}`,
      width: size.width,
      depth: size.depth,
      height: 0.1,
      x: v1.field.x,
      z: v1.field.z,
      rotation: v1.field.rotation,
      fieldKind: v1.field.kind,
    });
  }
  return {
    version: 2,
    lot: v1.lot,
    hour: v1.hour ?? 10,
    north: 0,
    showInteriors: false,
    showNames: true,
    showMeasures: true,
    items,
  };
}
